import { Button, Group, SimpleGrid, Stack, Card, Text } from "@mantine/core";
import { createStyles } from "@mantine/emotion";
import {
  IconCalendar,
  IconCalendarEvent,
  IconUsers,
  IconSettings,
  IconChartBar,
  IconClipboardList,
} from "@tabler/icons-react";
import { gql } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import {
  Tournament,
  TournamentTeams as TournamentTeamsType,
} from "../../../../gql/graphql";
import { useNavigate, useParams } from "react-router-dom";
import { DetailPageHeader } from "../../components/PageHeader";
import { StatCard, TournamentStatCard } from "../../components/StatCard";
import { StatusBadge } from "../../components/StatusBadge";
import { DetailPageSkeleton } from "../../components/SkeletonLoader";
import {
  useOrganizationLimits,
  formatLimitValue,
} from "../../../../business/limits.hook";

const useStyles = createStyles((theme) => ({
  infoCard: {
    backgroundColor: theme.colors.dark[7],
    border: `1px solid ${theme.colors.dark[5]}`,
  },
  infoLabel: {
    fontSize: theme.fontSizes.xs,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: theme.colors.dark[2],
  },
  infoValue: {
    fontSize: theme.fontSizes.sm,
    fontWeight: 500,
    color: theme.colors.dark[0],
  },
  infoMissing: {
    fontSize: theme.fontSizes.sm,
    fontStyle: "italic",
    color: theme.colors.dark[3],
  },
}));

const deriveTournamentStatus = (tournament: Tournament): string => {
  const now = new Date();
  const startAt = tournament.startAt ? new Date(tournament.startAt) : null;
  const endAt = tournament.endAt ? new Date(tournament.endAt) : null;
  const startReg = tournament.startRegistrationsAt
    ? new Date(tournament.startRegistrationsAt)
    : null;
  const endReg = tournament.endRegistrationsAt
    ? new Date(tournament.endRegistrationsAt)
    : null;
  const visibleAt = tournament.visibleAt
    ? new Date(tournament.visibleAt)
    : null;

  if (endAt && now > endAt) return "finished";
  if (startAt && now >= startAt) return "live";
  if (startReg && endReg && now >= startReg && now < endReg)
    return "registration";
  if (startReg && now < startReg) return "upcoming";
  if (!visibleAt || now < visibleAt) return "draft";
  return "upcoming";
};

const formatDate = (date: string | null | undefined) => {
  if (!date) return null;
  return new Date(date).toLocaleString();
};

const daysUntil = (date: string | null | undefined): number | null => {
  if (!date) return null;
  const diff = new Date(date).getTime() - Date.now();
  if (diff < 0) return null;
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
};

export const TournamentDashboard = () => {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const tournamentId = useParams().tournamentId;

  const { data, loading, refetch } = useQuery<{
    tournament: Tournament;
  }>(
    gql`
      query ($id: ID!) {
        tournament(id: $id) {
          title
          visibleAt
          startRegistrationsAt
          endRegistrationsAt
          startAt
          endAt
          configuration {
            teamsCount
          }
          teams(status: CONFIRMED, page: { first: 1 }) {
            totalCount
          }
        }
      }
    `,
    { variables: { id: tournamentId } },
  );

  const { data: registeredTeams } = useQuery<{
    tournamentTeams: TournamentTeamsType;
  }>(
    gql`
      query ($id: ID!) {
        tournamentTeams(
          tournamentId: $id
          status: REGISTERED
          page: { first: 1 }
        ) {
          totalCount
        }
      }
    `,
    { variables: { id: tournamentId } },
  );

  const { data: stepsData } = useQuery<{
    tournamentSteps: { id: string }[];
  }>(
    gql`
      query ($tournamentId: ID!) {
        tournamentSteps(tournamentId: $tournamentId) {
          id
        }
      }
    `,
    { variables: { tournamentId } },
  );

  const { getLimit } = useOrganizationLimits([
    "TOURNAMENT_TEAMS_REGISTERED",
    "TOURNAMENT_STEPS",
    "TOURNAMENT_TEAM_SIZE",
  ]);

  if (loading) {
    return <DetailPageSkeleton />;
  }

  const teamsLimit = getLimit("TOURNAMENT_TEAMS_REGISTERED");
  const stepsLimit = getLimit("TOURNAMENT_STEPS");
  const teamSizeLimit = getLimit("TOURNAMENT_TEAM_SIZE");

  const tournament = data?.tournament;
  const confirmedCount = tournament?.teams?.totalCount ?? 0;
  const registeredCount = registeredTeams?.tournamentTeams?.totalCount ?? 0;
  const maxTeams = tournament?.configuration?.teamsCount ?? 0;
  const fillPct =
    maxTeams > 0 ? Math.min((confirmedCount / maxTeams) * 100, 100) : 0;
  const stepCount = stepsData?.tournamentSteps?.length ?? 0;
  const derivedStatus = tournament
    ? deriveTournamentStatus(tournament)
    : "draft";
  const daysToStart = daysUntil(tournament?.startAt);
  const daysToRegClose = daysUntil(tournament?.endRegistrationsAt);

  const isVisible =
    tournament?.visibleAt && new Date(tournament.visibleAt) <= new Date();
  const isRegOpen =
    tournament?.startRegistrationsAt &&
    new Date(tournament.startRegistrationsAt) <= new Date() &&
    (!tournament?.endRegistrationsAt ||
      new Date(tournament.endRegistrationsAt) > new Date());
  const isRegClosed =
    tournament?.endRegistrationsAt &&
    new Date(tournament.endRegistrationsAt) <= new Date();

  return (
    <div className="wp-fade-in">
      <DetailPageHeader
        title={tournament?.title ?? "Tournament"}
        status={derivedStatus}
        onEdit={() => navigate("settings/general")}
        onRefresh={() => refetch()}
        showBreadcrumbs={false}
      />

      {/* Stat Cards */}
      <SimpleGrid
        cols={{ base: 2, md: 4 }}
        className="wp-stagger-children"
        mb="lg"
      >
        <TournamentStatCard
          label="Confirmed Teams"
          value={confirmedCount}
          progress={fillPct}
          progressLabel={`${confirmedCount} / ${maxTeams} slots`}
          progressColor={fillPct >= 100 ? "green" : "cyan"}
          onClick={() => navigate("teams")}
          tooltip="Click to manage teams"
        />
        <StatCard
          label="Registered"
          value={registeredCount}
          iconType="users"
          description={
            teamsLimit
              ? `Max ${formatLimitValue(teamsLimit.max, teamsLimit.type)} per tournament`
              : "Awaiting confirmation"
          }
          onClick={() => navigate("teams")}
        />
        <StatCard
          label="Steps"
          value={stepCount}
          iconType="games"
          description={
            stepsLimit
              ? `Max ${formatLimitValue(stepsLimit.max, stepsLimit.type)} per tournament`
              : "Tournament stages"
          }
          onClick={() => navigate("steps")}
        />
        <StatCard
          label={daysToStart !== null ? "Days Until Start" : "Tournament Date"}
          value={
            daysToStart !== null
              ? daysToStart
              : tournament?.startAt
                ? (formatDate(tournament.startAt) ?? "-")
                : "-"
          }
          iconType="event"
          description={
            daysToRegClose !== null
              ? `Registration closes in ${daysToRegClose} days`
              : isRegClosed
                ? "Registration closed"
                : undefined
          }
        />
      </SimpleGrid>

      {/* Info Cards */}
      <SimpleGrid cols={{ base: 1, md: 2 }} mb="lg">
        <Card className={classes.infoCard} p="md" radius="lg">
          <Group justify="space-between" mb="md">
            <Text fw={600}>Tournament Info</Text>
            <StatusBadge
              status={isVisible ? "active" : "draft"}
              label={isVisible ? "Visible" : "Hidden"}
            />
          </Group>
          <SimpleGrid cols={2} spacing="md">
            <Stack gap={4}>
              <Group gap="xs">
                <IconCalendar size={14} color="gray" />
                <Text className={classes.infoLabel}>Start Date</Text>
              </Group>
              {tournament?.startAt ? (
                <Text className={classes.infoValue}>
                  {formatDate(tournament.startAt)}
                </Text>
              ) : (
                <Text className={classes.infoMissing}>Not set</Text>
              )}
            </Stack>
            <Stack gap={4}>
              <Group gap="xs">
                <IconCalendarEvent size={14} color="gray" />
                <Text className={classes.infoLabel}>End Date</Text>
              </Group>
              {tournament?.endAt ? (
                <Text className={classes.infoValue}>
                  {formatDate(tournament.endAt)}
                </Text>
              ) : (
                <Text className={classes.infoMissing}>Not set</Text>
              )}
            </Stack>
          </SimpleGrid>
        </Card>

        <Card className={classes.infoCard} p="md" radius="lg">
          <Group justify="space-between" mb="md">
            <Text fw={600}>Registration</Text>
            <StatusBadge
              status={
                isRegOpen
                  ? "open"
                  : isRegClosed
                    ? "closed"
                    : tournament?.startRegistrationsAt
                      ? "pending"
                      : "draft"
              }
            />
          </Group>
          <SimpleGrid cols={2} spacing="md">
            <Stack gap={4}>
              <Group gap="xs">
                <IconCalendar size={14} color="gray" />
                <Text className={classes.infoLabel}>Opens At</Text>
              </Group>
              {tournament?.startRegistrationsAt ? (
                <Text className={classes.infoValue}>
                  {formatDate(tournament.startRegistrationsAt)}
                </Text>
              ) : (
                <Text className={classes.infoMissing}>Not set</Text>
              )}
            </Stack>
            <Stack gap={4}>
              <Group gap="xs">
                <IconCalendarEvent size={14} color="gray" />
                <Text className={classes.infoLabel}>Closes At</Text>
              </Group>
              {tournament?.endRegistrationsAt ? (
                <Text className={classes.infoValue}>
                  {formatDate(tournament.endRegistrationsAt)}
                </Text>
              ) : (
                <Text className={classes.infoMissing}>Not set</Text>
              )}
            </Stack>
          </SimpleGrid>
        </Card>
      </SimpleGrid>

      {/* Quick Actions */}
      <Group mb="lg">
        <Button
          leftSection={<IconUsers size={16} />}
          variant="light"
          onClick={() => navigate("teams")}
          className="wp-btn-press"
        >
          Manage Teams
        </Button>
        <Button
          leftSection={<IconChartBar size={16} />}
          variant="light"
          onClick={() => navigate("steps")}
          className="wp-btn-press"
        >
          View Steps
        </Button>
        <Button
          leftSection={<IconClipboardList size={16} />}
          variant="light"
          onClick={() => navigate("settings/registration")}
          className="wp-btn-press"
        >
          Registration Settings
        </Button>
        <Button
          leftSection={<IconSettings size={16} />}
          variant="light"
          onClick={() => navigate("settings/custom-fields")}
          className="wp-btn-press"
        >
          Custom Fields
        </Button>
      </Group>
    </div>
  );
};
